<template>
  <div class="article-section">

    <div class="article-section__wrapper" :class="containerClass" ref="stickySidebar">
      <slot v-if="smallScreen" name="article-header"/>

      <nav v-if="this.$scopedSlots.nav"
           role="navigation"
           :aria-label="tableContents"
           class="article-section__sidebar"

           :class="[{'sticky-block__sidebar--right': sidebarPosition === 'right'}, positionClass]">

        <div class="article-section__sidebar-content"
             :class="[{'fixed': !smallScreen && fixed }, { 'shifted-top': shiftedTop && fixed }]"
             ref="sticky">
          <slot name="nav" :links="links" :current-anchor="currentAnchor"/>
        </div>
      </nav>


      <div class="article-section__content" ref="content">
        <slot v-if="!smallScreen" name="article-header" />
        <slot/>
      </div>
    </div>
  </div>
</template>
<script>
  import { eventBus } from '@EventBus';
  import { tag } from '@Foundation/analyticsHandler';
  import { inViewport, scrollPosition, smoothScroll } from '@Foundation/utilities';

  export default {
    name: 'article-section',
    directives: {
      tag
    },
    props: {
      sidebarPosition: {
        type: String,
        default: 'left'
      },
      headingSelector: {
        type: String,
        default: 'h2'
      },
      tableContents: {
        type: String,
        default: 'Table of contents'
      },
      containerClass: {
        type: String,
        default: ''
      },
      linkWithHtml: {
        type: Boolean,
        default: false
      },
      hasFixed: {
        type: Boolean,
        default: true
      }
    },

    data() {
      return {
        fixed: false,
        shiftedTop: false,
        positionClass: '',
        links: [],
        currentAnchor: undefined,
        smallScreen: !window.matchMedia('(min-width: 1024px)').matches,
        lastScrollTop: window.pageYOffset || document.documentElement.scrollTop
      };
    },

    mounted() {
      this.links = this.findLinks();

      this.scrollListener = this.onScroll.bind(this);
      window.addEventListener('scroll', this.scrollListener);

      this.resizeListener = this.stickyPosition.bind(this);
      window.addEventListener('resize', this.resizeListener);

      this.screenCheck();
      this.stickyPosition();

      this.$root.$on('menu-hidden', (payload) => {
        payload ? this.shiftedTop = false : this.shiftedTop = true;
      });
    },

    destroyed() {
      window.removeEventListener('scroll', this.scrollListener);
      window.removeEventListener('resize', this.resizeListener);
    },

    methods: {
      findLinks() {
        const hash = {};
        const headings = this.$refs.content.querySelectorAll(this.headingSelector);

        return Array.from(headings).map((elTitle) => (
          this.createLinkFromHeading(elTitle, hash)
        ));
      },

      createLinkFromHeading(elTitle, hash) {
        const { linkLabel, title, taggingString } = this.getHeadingText(elTitle);
        const sanitizedTitle = this.sanitizeTitle(title);
        const id = this.createAnchorId(sanitizedTitle, hash);

        this.setupHeadingAnchor(elTitle, id);

        const tagging = this.isSearchSection()
          ? this.buildSearchTagging(taggingString, sanitizedTitle)
          : undefined;

        return {
          href: `#${id}${hash[id] || ''}`,
          label: linkLabel.replace('\n', ' '),
          tagging,
          scrollTo: (event) => this.handleLinkScroll(elTitle, event),
        };
      },

      getHeadingText(elTitle) {
        const rawTitle = this.linkWithHtml ? elTitle.innerHTML : elTitle.innerText;
        const normalized = rawTitle.replace(/\s{2,}/g, ' ');

        return {
          linkLabel: normalized.trim(),
          title: normalized,
          taggingString: normalized,
        };
      },

      sanitizeTitle(title) {
        return this.stripHtmlTags(title)
          .replaceAll('(', '')
          .replaceAll(')', '')
          .replace(/\d/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      },

      stripHtmlTags(value) {
        let result = '';
        let inTag = false;

        for (const char of value) {
          if (char === '<') {
            inTag = true;
          } else if (char === '>') {
            inTag = false;
          } else if (!inTag) {
            result += char;
          }
        }

        return result;
      },

      createAnchorId(title, hash) {
        const id = title.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s/g, '-');
        hash[id] = hash[id] === undefined ? 0 : hash[id]++;
        return id;
      },

      setupHeadingAnchor(elTitle, id) {
        elTitle.classList.add('heading--anchor');
        elTitle.setAttribute('id', id);
        elTitle.setAttribute('tabindex', '-1');
      },

      isSearchSection() {
        return this.$refs.stickySidebar.classList.contains('article-section--search');
      },

      buildSearchTagging(taggingString, title) {
        const taggingLabel = this.extractTaggingLabels(taggingString);

        return {
          action: title,
          label: taggingLabel[0],
        };
      },

      extractTaggingLabels(taggingString) {
        const strongTags = taggingString.match(/<strong>([^<]*)<\/strong>/gi) || [];
        const taggingLabel = [];

        strongTags.forEach((val) => {
          const digits = val.match(/\d/g);
          if (digits !== null) {
            taggingLabel.push(digits.join(''));
          }
        });

        return taggingLabel;
      },

      handleLinkScroll(elTitle, event) {
        this.scrollTo(elTitle).then(() => {
          this.updateCurrentAnchorFromEvent(event);
        });
      },

      updateCurrentAnchorFromEvent(event) {
        const href = event.target.getAttribute('href');
        this.currentAnchor = this.links.findIndex((element) => element.href === href);
      },

      scrollTo(el) {
        return new Promise((resolve) => {
          smoothScroll(el);
          /* istanbul ignore next */
          setTimeout(() => {
            resolve();
          }, 500);
        });
      },

      scrollDirection() {
        let direction;
        const st = window.pageYOffset || document.documentElement.scrollTop; // Credits: "https://github.com/qeremy/so/blob/master/so.dom.js#L426"
        if (st > this.lastScrollTop) {
            direction = 'down';
        } else {
            direction = 'up';
        }
        this.lastScrollTop = st <= 0 ? 0 : st; // For Mobile or negative scrolling
        return direction;
      },

      addFloating(target, position) {
        const targetStart = target.offsetTop;
        const targetEnd = (targetStart + target.clientHeight);
        let sideBarEnd;
        this.scrollDirection() === 'up' ? sideBarEnd = this.$refs.sticky.clientHeight + window.pageYOffset + 100 : sideBarEnd = this.$refs.sticky.clientHeight + window.pageYOffset;
        return (targetStart < position && sideBarEnd < targetEnd);
      },

      removeFloating(target, position) {
        const targetStart = target.offsetTop;
        const targetEnd = (targetStart + target.clientHeight);
        const sideBarEnd = this.$refs.sticky.clientHeight + window.pageYOffset;
        return (targetEnd < sideBarEnd || targetStart > position) ? { remove: true, position: targetEnd <= sideBarEnd + 95 ? 'bottom' : '' } : { remove: false };
      },

      getPaddings(elem) {
        const padLeft = parseInt(window.getComputedStyle(document.querySelector(elem), null).paddingLeft, 10);
        const padRight = parseInt(window.getComputedStyle(document.querySelector(elem), null).paddingRight, 10);
        return padLeft + padRight;
      },

      stickyPosition() {
        const stickyPos = this.$refs.stickySidebar.getBoundingClientRect();
        const paddings = this.getPaddings('.article-section__wrapper');

        /*istanbul ignore else*/
        if (this.fixed && !this.smallScreen) {
          this.$refs.sticky.style.width = `${stickyPos.width - this.$refs.content.clientWidth - paddings}px`;
        }
      },

      onScroll() {
        if (this.smallScreen) {
          return;
        }
        /*istanbul ignore else*/
        if (this.hasFixed) {
          this.updateSticky();
          this.updateHighlightedAnchor();
        }
      },

      updateSticky() {
        /*istanbul ignore else*/
        if (this.addFloating(this.$refs.content, scrollPosition())) {
          this.fixed = true;
          this.$nextTick(() => this.stickyPosition());
        } else if (this.removeFloating(this.$refs.content, scrollPosition()).remove) {
          this.positionClass = this.removeFloating(this.$refs.content, scrollPosition()).position;
          this.fixed = false;
        }
      },

      updateHighlightedAnchor() {
        Array.prototype.map
        .call(this.$refs.content.querySelectorAll(this.headingSelector), (elTitle, index) => {

          const oRect = elTitle.getBoundingClientRect();
          const oTop = oRect.top;

          if (oTop > 0 && oTop <= window.innerHeight * 15 / 100 && inViewport(elTitle) && this.currentAnchor !== index) {
            this.currentAnchor = index;
          }
        });
      },

      screenCheck() {
        eventBus.$on('mediaquery::changed', mq => {
          this.smallScreen = !!(mq.size === 'small' || mq.size === 'medium');
        });
      }
    }
  };
</script>
<style lang='scss' src="./article-section.scss"></style>
